import { useState } from "react";
import type { GameDetail, PublicPlayer, RoundView } from "backend/types";
import { api } from "./api";
import { Avatar, usePoll } from "./ui";
import { Chat } from "./Chat";
import { RoundEnd } from "./Guess";
import { Leaderboard } from "./Leaderboard";

export function PlayerGame({
  game,
  me,
  onLeave,
}: {
  game: GameDetail;
  me: PublicPlayer;
  onLeave: () => void;
}) {
  const { data } = usePoll(() => api.getRound(game.id), 2000, [game.id]);
  const [revealedRoundId, setRevealedRoundId] = useState<string | null>(null);

  const round = data?.round ?? null;

  if (round && round.status === "active") {
    if (revealedRoundId !== round.roundId) {
      return <Reveal round={round} onStart={() => setRevealedRoundId(round.roundId)} />;
    }
    return <Chat round={round} me={me} />;
  }

  if (round && round.status === "ended") {
    return <RoundEnd round={round} game={game} me={me} />;
  }

  return <PlayerLobby game={game} me={me} onLeave={onLeave} />;
}

function PlayerLobby({
  game,
  me,
  onLeave,
}: {
  game: GameDetail;
  me: PublicPlayer;
  onLeave: () => void;
}) {
  const mine = game.players.find((p) => p.playerId === me.id);
  const players = game.players.filter((p) => !p.isHost);
  const ended = game.status === "ended";

  return (
    <div className="mx-auto w-full max-w-xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onLeave} className="text-sm text-ink/50 hover:text-ink">← Rooms</button>
        <h1 className="font-display text-xl font-bold">{game.name}</h1>
        <span />
      </div>

      {ended ? (
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 text-center">
          <div className="text-2xl">🏁</div>
          <div className="font-display text-lg font-bold mt-1">Game over!</div>
        </div>
      ) : (
        <ProductCard game={game} product={mine?.product ?? ""} />
      )}

      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4">
        <div className="text-xs uppercase tracking-wide text-ink/40 mb-3">
          {players.length} player{players.length === 1 ? "" : "s"} in the room
        </div>
        <div className="flex flex-wrap gap-3">
          {players.map((p) => (
            <div key={p.playerId} className="flex flex-col items-center w-16 text-center">
              <Avatar color={p.color} size={40} ring={p.playerId === me.id} />
              <span className="text-xs mt-1 truncate w-full">{p.name}</span>
              {p.productReady ? (
                <span className="text-[10px] text-wa">ready</span>
              ) : (
                <span className="text-[10px] text-ink/30">…</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {game.roundCount > 0 && <Leaderboard gameId={game.id} highlightId={me.id} />}

      {!ended && (
        <p className="text-center text-sm text-ink/40">⏳ waiting for the host to start a round…</p>
      )}
    </div>
  );
}

function ProductCard({ game, product }: { game: GameDetail; product: string }) {
  const [value, setValue] = useState(product);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.setProduct(game.id, { product: value.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 space-y-2">
      <h2 className="font-display font-bold">🛍️ Your stall</h2>
      <p className="text-xs text-ink/50">What do you sell? Just a few words — no description.</p>
      <div className="flex gap-2">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. handmade planters"
          maxLength={60}
          className="flex-1 rounded-xl bg-parchment-deep/50 px-3 py-2 outline-none focus:ring-2 focus:ring-wa"
        />
        <button
          disabled={busy || !value.trim()}
          className="rounded-xl bg-wa text-white px-4 py-2 text-sm font-semibold hover:bg-wa-dark disabled:opacity-50"
        >
          {saved ? "Saved ✓" : "Save"}
        </button>
      </div>
      {err && <p className="text-xs text-red-500">{err}</p>}
    </form>
  );
}

function Reveal({ round, onStart }: { round: RoundView; onStart: () => void }) {
  const isSeller = round.role === "seller";
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-5">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-black/5 shadow-xl p-7 text-center space-y-5">
        <div className="text-xs uppercase tracking-widest text-ink/40">
          Round {round.index} · {round.mode === "postsale" ? "after-sale" : "fresh inquiry"}
        </div>
        <div>
          <div className="text-4xl">{isSeller ? "🧑‍💼" : "🛒"}</div>
          <div className="font-display text-2xl font-bold mt-2">
            You're the {isSeller ? "SELLER" : "CUSTOMER"}
          </div>
        </div>

        <div className="rounded-2xl bg-parchment-deep/50 p-4 text-sm">
          {isSeller ? (
            <p>You're selling <span className="font-semibold">{round.product}</span>.</p>
          ) : (
            <p>The shop sells <span className="font-semibold">{round.product}</span>.</p>
          )}
        </div>

        <div className="rounded-2xl bg-spice/10 p-4 text-sm text-left">
          <div className="text-[11px] uppercase tracking-wide text-spice font-semibold mb-1">
            You are: {round.persona.label}
          </div>
          <p className="text-ink/70">{round.persona.instruction}</p>
        </div>

        {round.context && (
          <div className="rounded-2xl bg-gold/15 p-4 text-sm text-left">
            <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
              {round.mode === "postsale" ? "Your order" : "Why you're here"}
            </div>
            <p className="text-ink/70">{round.context}</p>
          </div>
        )}

        <button
          onClick={onStart}
          className="w-full rounded-xl bg-wa text-white px-4 py-2.5 font-semibold hover:bg-wa-dark transition"
        >
          Start chatting ▸
        </button>
      </div>
    </div>
  );
}
